import { Link } from 'react-router-dom';
import { Cookie, FileLock2, LogOut, Settings, ShieldCheck } from 'lucide-react';
import { config } from '@/config/env';
import { useAuth, useAuthorization } from '@/features/auth/hooks';
import { useConsent } from '@/features/consent/hooks/useConsent';
import { broadcastShellLogout } from '@/features/embedding/utils/logout';
import { useShellI18n } from '@/features/language/useI18n';
import { serviceNavItemIds } from '@/features/navigation/config';
import { getNavIcon } from '@/features/navigation/icons';

const LOGOUT_PROPAGATION_DELAY_MS = 120;

interface ServiceLink {
  id: string;
  title: string;
  description: string;
  to: string;
}

interface OrderedServiceLink extends ServiceLink {
  sortOrder: number;
  index: number;
}

const serviceRoutes: Record<string, Pick<ServiceLink, 'description' | 'to'>> = {
  academy: {
    to: '/academy',
    description: 'A knowledge base where WIANCO is publishing blogposts to various topics.',
  },
  community: {
    to: '/community',
    description:
      'A discussion space for everyone to ask questions, share best practices, and discover the most helpful answers and ideas.',
  },
  launchpad: {
    to: '/launchpad',
    description: 'Open Launchpad inside the portal workspace.',
  },
  support: {
    to: '/support',
    description: 'Create and manage your support requests.',
  },
  shop: {
    to: '/shop',
    description: 'Review account-specific offers and prepare HubSpot-backed order requests.',
  },
  portfolio: {
    to: '/portfolio',
    description: 'Review your deals, products, and associated companies.',
  },
  marketplace: {
    to: '/marketplace',
    description: 'Discover process solutions, modules, assets and bundles for EMMA.',
  },
  library: {
    to: '/library',
    description: 'Browse and download the documents available to your account.',
  },
  booking: {
    to: '/booking',
    description: 'Plan and manage customer training bookings.',
  },
};

function getServiceLinks(
  embeddedServices: ReturnType<typeof config>['EMBEDDED_SERVICES']
): ServiceLink[] {
  const serviceOrder = new Map(serviceNavItemIds.map((id, index) => [id, index]));

  return embeddedServices
    .map<OrderedServiceLink>((service, index) => {
      const knownService = serviceRoutes[service.id];

      return {
        id: service.id,
        title: service.id === 'launchpad' ? 'Launchpad' : service.name,
        description: knownService?.description ?? 'Open {{name}} inside the portal workspace.',
        to: knownService?.to ?? `/apps/${service.id}`,
        sortOrder: serviceOrder.get(service.id) ?? Number.POSITIVE_INFINITY,
        index,
      };
    })
    .sort((left, right) => {
      if (left.sortOrder !== right.sortOrder) {
        return left.sortOrder - right.sortOrder;
      }

      return left.index - right.index;
    })
    .map<ServiceLink>(({ id, title, description, to }) => ({ id, title, description, to }));
}

export const HomePage = () => {
  const { t } = useShellI18n();
  const { filterServices } = useAuthorization();
  const { logout } = useAuth();
  const { openSettings } = useConsent();
  const serviceLinks = getServiceLinks(filterServices(config().EMBEDDED_SERVICES || []));

  const handleLogout = () => {
    broadcastShellLogout();
    window.setTimeout(() => {
      void logout();
    }, LOGOUT_PROPAGATION_DELAY_MS);
  };

  return (
    <section className="page-shell fill-height">
      {/*
        Der Seitenkopf traegt keine Flaeche. Eine Box um zwei Zeilen Text ist auf
        Brand-Black ein brauner Balken und konkurriert mit den Karten darunter,
        die das eigentliche Ziel der Seite sind.
      */}
      <header className="page-intro">
        <h1 className="page-title">{t('Welcome to the WIANCO Portal')}</h1>
        <p className="page-subtitle">
          {t('Everything around EMMA and our product worlds, in one place.')}
        </p>
      </header>

      {/*
        Kein Panel um die Karten: die Karten tragen schon eine Flaeche, eine zweite
        darunter verdoppelt den Glas-Tint und faerbt die Seite braun.
      */}
      <article className="section" aria-labelledby="overview-services">
        <h2 id="overview-services" className="sr-only">
          {t('Services')}
        </h2>

        {serviceLinks.length > 0 ? (
          <div className="overview-grid">
            {serviceLinks.map((service) => {
              const title = t(service.title);
              const ServiceIcon = getNavIcon(service.id);

              return (
                <Link key={service.id} className="overview-card" to={service.to}>
                  <span className="overview-card-glow" aria-hidden="true" />
                  <span className="overview-card-icon" aria-hidden="true">
                    <ServiceIcon />
                  </span>
                  {/*
                    Titel und Beschreibung brauchen diese Huelle: als direkte
                    Flex-Kinder der Karte wuerde der Browser ihr display zu
                    flow-root blockifizieren, und -webkit-line-clamp greift dann
                    nicht mehr.
                  */}
                  <span className="overview-card-body">
                    <strong className="overview-card-title">{title}</strong>
                    <span className="overview-card-description">
                      {t(service.description, { name: title })}
                    </span>
                  </span>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">{t('No services configured in runtime config.')}</div>
        )}
      </article>

      {/*
        Die Ueberschrift bleibt fuer Screenreader und haelt die Gliederung der Seite
        intakt, tritt aber optisch zurueck. Die Zeichen sind dekorativ und duerfen
        nichts zum zugaenglichen Namen der Aktionen beitragen.
      */}
      <article className="overview-footer" aria-labelledby="overview-user-actions">
        <h2 id="overview-user-actions" className="sr-only">
          {t('User actions')}
        </h2>

        <Link className="footer-action" to="/settings">
          <Settings aria-hidden="true" />
          {t('Settings')}
        </Link>
        <button type="button" className="footer-action" onClick={openSettings}>
          <Cookie aria-hidden="true" />
          {t('Cookies')}
        </button>
        <Link className="footer-action" to="/privacy-policy">
          <ShieldCheck aria-hidden="true" />
          {t('Data Privacy')}
        </Link>
        <Link className="footer-action" to="/data-protection">
          <FileLock2 aria-hidden="true" />
          {t('Data Protection')}
        </Link>
        <button type="button" className="footer-action" onClick={handleLogout}>
          <LogOut aria-hidden="true" />
          {t('Logout')}
        </button>
      </article>
    </section>
  );
};

export default HomePage;
